import React, { useEffect, useState } from "react";
import sanityClient from "../client.js";

export default function Project() {
  const [projectData, setProjectData] = useState(null);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "project"] | order(_createdAt desc) {
          _createdAt,
          title,
          technology,
          description,
          link,
          github
        }`
      )
      .then((data) => setProjectData(data))
      .catch(console.error);
  }, []);

  return (
    <main className="proj-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Sora:wght@700;800&display=swap');

        .proj-root {
          min-height: 100vh;
          background-color: #080e1c;
          font-family: 'Inter', system-ui, sans-serif;
          position: relative;
          overflow: hidden;
        }

        .proj-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 50% at 80% 10%, rgba(56,189,248,0.08) 0%, transparent 60%),
            radial-gradient(ellipse 70% 50% at 5% 80%, rgba(99,102,241,0.1) 0%, transparent 55%);
          pointer-events: none;
          z-index: 0;
        }

        .proj-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 4rem 2rem 6rem;
          position: relative;
          z-index: 1;
        }

        /* Page header */
        .proj-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }

        .proj-eyebrow {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #38bdf8;
          margin-bottom: 1rem;
        }

        .proj-title {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 800;
          color: #f0f4ff;
          letter-spacing: -0.02em;
          margin-bottom: 0.75rem;
        }

        .proj-title span {
          background: linear-gradient(90deg, #818cf8, #38bdf8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .proj-subtitle {
          color: #475569;
          font-size: 0.95rem;
        }

        /* Grid */
        .proj-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        /* Card */
        .proj-card {
          background: #0f1829;
          border: 1px solid rgba(99,102,241,0.12);
          border-radius: 16px;
          padding: 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
          position: relative;
          overflow: hidden;
        }

        .proj-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #6366f1, #38bdf8);
          opacity: 0;
          transition: opacity 0.25s;
        }

        .proj-card:hover {
          border-color: rgba(99,102,241,0.3);
          box-shadow: 0 12px 40px rgba(0,0,0,0.45), 0 0 30px rgba(99,102,241,0.08);
          transform: translateY(-3px);
        }

        .proj-card:hover::before {
          opacity: 1;
        }

        /* Card top row */
        .proj-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.75rem;
        }

        .proj-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: rgba(99,102,241,0.12);
          border: 1px solid rgba(99,102,241,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #818cf8;
        }

        /* Link icons top-right */
        .proj-links-top {
          display: flex;
          gap: 0.5rem;
        }

        .proj-icon-link {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(99,102,241,0.08);
          border: 1px solid rgba(99,102,241,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          text-decoration: none;
          transition: color 0.2s, background 0.2s, border-color 0.2s;
        }

        .proj-icon-link:hover {
          color: #a5b4fc;
          background: rgba(99,102,241,0.15);
          border-color: rgba(99,102,241,0.35);
        }

        .proj-card-title {
          font-family: 'Sora', system-ui, sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: #e2e8f0;
          text-decoration: none;
          transition: color 0.2s;
          line-height: 1.3;
        }

        .proj-card-title:hover { color: #a5b4fc; }

        .proj-description {
          color: #64748b;
          font-size: 0.875rem;
          line-height: 1.75;
          flex: 1;
        }

        /* Tech tags */
        .proj-tech-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
        }

        .proj-tech-tag {
          font-size: 0.68rem;
          font-weight: 500;
          color: #38bdf8;
          background: rgba(56,189,248,0.08);
          border: 1px solid rgba(56,189,248,0.18);
          padding: 0.2rem 0.55rem;
          border-radius: 5px;
          letter-spacing: 0.03em;
        }

        /* Card bottom links */
        .proj-card-links {
          display: flex;
          gap: 1.25rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(99,102,241,0.08);
        }

        .proj-card-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.8rem;
          font-weight: 500;
          color: #475569;
          text-decoration: none;
          transition: color 0.2s;
        }

        .proj-card-link:hover { color: #a5b4fc; }

        /* Loading skeleton */
        .proj-skeleton {
          background: #0f1829;
          border: 1px solid rgba(99,102,241,0.08);
          border-radius: 16px;
          height: 220px;
          animation: shimmer 1.5s ease infinite;
        }

        @keyframes shimmer {
          0%, 100% { opacity: 0.5; }
          50%       { opacity: 0.9; }
        }

        @media (max-width: 640px) {
          .proj-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="proj-container">
        {/* Header */}
        <div className="proj-header">
          <span className="proj-eyebrow">Portfolio</span>
          <h1 className="proj-title">
            My <span>Projects</span>
          </h1>
          <p className="proj-subtitle">
            Things I've built, shipped, and learned from
          </p>
        </div>

        {/* Grid */}
        {!projectData ? (
          <div className="proj-grid">
            {[1, 2, 3, 4].map(i => <div key={i} className="proj-skeleton" />)}
          </div>
        ) : (
          <div className="proj-grid">
            {projectData.map((project, index) => {
              // Parse comma-separated tech string into tags
              const techTags = project.technology
                ? project.technology.split(",").map(t => t.trim()).filter(Boolean)
                : [];

              return (
                <article className="proj-card" key={index}>
                  <div className="proj-card-top">
                    {/* Folder icon */}
                    <div className="proj-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>

                    {/* Quick-link icons */}
                    <div className="proj-links-top">
                      {project.github && (
                        <a href={project.github} target="_blank" rel="noopener noreferrer" className="proj-icon-link" title="GitHub">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.483 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                          </svg>
                        </a>
                      )}
                      {project.link && (
                        <a href={project.link} target="_blank" rel="noopener noreferrer" className="proj-icon-link" title="Live demo">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <a
                    href={project.link || project.github || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="proj-card-title"
                  >
                    {project.title}
                  </a>

                  {/* Description */}
                  <p className="proj-description">{project.description}</p>

                  {/* Tech tags */}
                  {techTags.length > 0 && (
                    <div className="proj-tech-row">
                      {techTags.map(tag => (
                        <span key={tag} className="proj-tech-tag">{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Bottom links */}
                  <div className="proj-card-links">
                    {project.link && (
                      <a href={project.link} target="_blank" rel="noopener noreferrer" className="proj-card-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        Live demo
                      </a>
                    )}
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="proj-card-link">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.483 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                        </svg>
                        GitHub
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}